// @flow

import React, { Component } from 'react';
import styled, { css } from 'styled-components';

import type { Dictionary } from '../utils/vortex/types';

import colors from '../config/colors';

import Input from './Input';
import Icon from './Icon';

const SpecAbbr = styled.abbr`
  color: ${colors.text.light};
  display: inline-block;
  font-size: 11px;
  line-height: 1;
  text-transform: uppercase;
  text-align: right;
  white-space: nowrap;
  width: 10%;
`;

// TODO: w/o flexbox
const SpecInputWrapper = styled.div`
  border-bottom: 1px solid ${colors.input.border};
  padding-top: 0.5em;
  position: relative;

  ${props => props.hidden && css`
    display: none;
  `}

  label {
    color: ${colors.text.light};
    cursor: help;
    display: inline-block;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    padding: 0.75em 0;
    width: ${props => props.select ? '100%' : '60%' };
  }

  select, input, textarea {
    &:focus {
      outline-color: ${colors.vortex.brand};
    }
  }

  input, select {
    background: none;
    border: none;
    box-shadow: none;
    color: black;
    -webkit-appearance: none;
  }

  select {
    margin-bottom: 0.25em;
    position: relative;
    width: 100%;
    z-index: 1;
  }

  .ss-navigatedown {
    bottom: 0;
    color: ${colors.text.light};
    display: block;
    font-size: 11px;
    position: absolute;
    right: 0;
    text-align: right;
    width: 10%;
  }

  ${Input} {
    font-size: 18px;
    padding: 0.5em 0.25em;
    text-align: right;
    width: 30%;
  }

  ${props => props.message && css`
    border-color: ${props => props.theme.input[props.message.type].border};
    color: ${props => props.theme.input[props.message.type].color};
    abbr, ${Input}, label, select, span, .ss-navigatedown {
      color: ${props => props.theme.input[props.message.type].color};
    }
  `}

  ${props => !props.select && window.Modernizr.flexbox && css`
    > .flex {
      align-items: center;
      display: flex;
    }
  `}

  @media (min-width: 375px) {
    label {
      font-size: 14px;
    }
  }
`;

const InfoMessage = styled.div`
  color: ${colors.text.light};
  font-size: 14px;
  font-style: italic;
  line-height: 1.4;
  margin-bottom: 0.5em;
  padding-left: 20px;
  position: relative;
  .ss-icon {
    left: 0;
    position: absolute;
  }
`;

function selectVal(key: string, values: Dictionary) {
  return `${values[key]}: ${key}`;
}

export type Message = {
  type: 'error' | 'warning',
  text: string
}

type Props = {|
  abbr: ?string,
  autofocus?: boolean,
  hidden?: boolean,
  info: string,
  message: Message,
  onChange: (string, mixed | void) => void,
  spec: string,
  title: string,
  value: number | string,
  values: ?Dictionary
|};

type State = {
  tooltipShown: boolean
};

class SpecInput extends Component<Props, State> {

  static defaultProps = {
    autofocus: false,
    hidden: false
  }

  state = {
    tooltipShown: false
  }

  inputControl: ?(HTMLInputElement | HTMLSelectElement)

  componentDidMount = () => {
    if (this.inputControl != null && this.props.autofocus) {
      this.inputControl.focus();
    }
  }

  onChangeHandler = (event: SyntheticEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.currentTarget.value;
    // console.log('onChangeHandler', value);
    if (this.props.onChange) {
      this.props.onChange(this.props.spec, value);
    }
  }

  onClicked = (event: SyntheticEvent<>) => {
    if (this.inputControl) {
      this.inputControl.focus();
    }
  }

  onFocusHandler = (event: SyntheticFocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  }

  onLabelClicked = (event: SyntheticEvent<>) => {
    this.setState({ tooltipShown: !this.state.tooltipShown });
  }

  renderControl() {
    let value = this.props.value;
    const values = this.props.values;

    // <select> with values
    if (values != null) {
      const keys = Object.keys(values);
      value = value || selectVal(keys[0], values);
      return (
        <select
          value={value}
          onChange={this.onChangeHandler}
        >
          {keys.map(key => <option key={key} value={selectVal(key, values)}>{key}</option>)}
        </select>
      );
    }

    // <input> with value
    return (
      <Input
        type="number"
        value={value || ''}
        onChange={this.onChangeHandler}
        onFocus={this.onFocusHandler}
        onContextMenu={e => e.preventDefault()}
        innerRef={ref => this.inputControl = ref}
      />
    );
  }

  renderMessages() {
    const { message } = this.props;
    if (message) {
      return (
        <InfoMessage type={message.type}>
          <Icon type={message.type === 'error' ? 'alert' : 'flag'} withMargin={true} />
          <span>{message.text}</span>
        </InfoMessage>
      );
    }
    else if (this.state.tooltipShown) {
      return (
        <InfoMessage onClick={this.onLabelClicked} style={{ cursor: 'pointer' }}>
          <Icon type="info" withMargin={true} />
          <span>{this.props.info}</span>
        </InfoMessage>
      );
    }
  }

  render() {
    const { abbr, message, title, values } = this.props;
    return (
      <SpecInputWrapper
        hidden={this.props.hidden}
        message={message}
        onClick={this.onClicked}
        select={!!values}
        tooltipShown={this.state.tooltipShown}
      >
        <div className="flex">
          <label onClick={this.onLabelClicked}>{title}</label>
          {this.renderControl()}
          {abbr ? <SpecAbbr>{abbr}</SpecAbbr> : <span className="ss-navigatedown"></span>}
        </div>
        {this.renderMessages()}
      </SpecInputWrapper>
    );
  }

}

export default SpecInput;



// WEBPACK FOOTER //
// ./src/components/SpecInput.js